import React, { useEffect, useMemo, useState } from 'react';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';


const TEAM_MEMBERS = [
  'Dimitri DE BACKER',
  'Aurélien BARBIEUX',
  'Jonathan DEMORTIER',
  'Leslie BASHORUN',
  'Mohamed ATAUIL',
  'Pierre BUIDIN',
];

function localMonthStr(date) {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0')].join('-');
}

function shiftMonth(month, amount) {
  const [year, monthNumber] = month.split('-').map(Number);
  return localMonthStr(new Date(year, monthNumber - 1 + amount, 1));
}

function decimalHours(seconds) {
  return (seconds / 3600).toFixed(2);
}

function exportCSV(data, member) {
  const rows = [
    ['Member', 'Date', 'Weekday', 'Hours', 'Decimal'],
    ...data.days.map(d => [member, d.date, d.weekday, d.display, decimalHours(d.seconds)]),
    [],
    ['Total', '', '', data.total_display, decimalHours(data.total_seconds)],
    ['Days logged', '', '', data.days_logged, ''],
    ['Average/day', '', '', data.average_display, ''],
  ];
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `time-report-${member.split(' ').pop()}-${data.month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function DayDetail({ date, member, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/jira/worklogs/report/day?date=${date}&member_name=${encodeURIComponent(member)}`)
      .then(r => r.json())
      .then(d => { setDetail(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [date, member]);

  const label = new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface)', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        width: '100%', maxWidth: 680, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{label}</div>
            {detail && <div style={{ color: 'var(--text-2)', fontSize: 12, marginTop: 3 }}>
              {detail.entries.length} entr{detail.entries.length === 1 ? 'y' : 'ies'} · Total: <strong>{detail.total_display}</strong>
            </div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-2)', lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-2)' }}>Loading…</div>}
          {!loading && detail && detail.entries.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-2)' }}>No entries found for this day.</div>
          )}
          {!loading && detail && detail.entries.map((e, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '1fr auto',
              alignItems: 'center', gap: 12,
              padding: '12px 22px', borderBottom: '1px solid var(--border)',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  {e.url
                    ? <a href={e.url} target="_blank" rel="noopener noreferrer" className="badge badge-blue" style={{ textDecoration: 'none', fontSize: 11 }}>{e.issue_key}</a>
                    : <span className="badge badge-blue" style={{ fontSize: 11 }}>{e.issue_key}</span>
                  }
                  {e.status && <span className="badge badge-default" style={{ fontSize: 10 }}>{e.status}</span>}
                  {e.project_name && <span style={{ color: 'var(--text-2)', fontSize: 11 }}>{e.project_name}</span>}
                  {e.started_time && <span style={{ color: 'var(--text-2)', fontSize: 11, marginLeft: 4 }}>🕐 {e.started_time}</span>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{e.summary}</div>
              </div>
              <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <div style={{ fontFamily: 'DM Mono', fontWeight: 700, fontSize: 14 }}>{e.display}</div>
                <div style={{ fontFamily: 'DM Mono', color: 'var(--text-2)', fontSize: 11 }}>{decimalHours(e.seconds)}h</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MyReportPage() {
  const [month, setMonth] = useState(() => localMonthStr(new Date()));
  const [member, setMember] = useState(TEAM_MEMBERS[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | 'ok' | 'err'

  const sendEmail = () => {
    setEmailSending(true);
    setEmailStatus(null);
    fetch(`${API}/api/jira/worklogs/report/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ month, member_name: member }),
    })
      .then(r => r.json())
      .then(d => { setEmailStatus(d.ok ? 'ok' : 'err'); })
      .catch(() => setEmailStatus('err'))
      .finally(() => { setEmailSending(false); setTimeout(() => setEmailStatus(null), 4000); });
  };

  const printReport = () => {
    if (!data) return;
    const maxS = Math.max(...data.days.map(d => d.seconds), 1);
    const rows = data.days.map(d => {
      const pct = Math.min(Math.round((d.seconds / maxS) * 100), 100);
      const color = pct > 90 ? '#c62828' : pct > 60 ? '#e65100' : '#2563eb';
      return `<tr>
        <td>${d.date}</td>
        <td style="color:#64748b">${d.weekday}</td>
        <td><div style="height:8px;border-radius:99px;background:#e8eaf6;width:200px">
          <div style="height:100%;width:${pct}%;border-radius:99px;background:${color}"></div></div></td>
        <td style="text-align:right;font-family:monospace;font-weight:700">${d.display}</td>
        <td style="text-align:right;font-family:monospace;color:#64748b">${(d.seconds/3600).toFixed(2)}h</td>
      </tr>`;
    }).join('');
    const firstName = member.split(' ')[0];
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Time Report — ${firstName} — ${data.label}</title>
      <style>
        body{font-family:'Segoe UI',Arial,sans-serif;margin:32px;color:#1e293b}
        h1{font-size:22px;margin:0}p{color:#64748b;margin:4px 0 24px}
        .summary{display:flex;gap:32px;margin-bottom:24px}
        .summary div{text-align:center}
        .summary .val{font-size:28px;font-weight:700;font-family:monospace}
        .summary .lbl{font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.5px}
        table{width:100%;border-collapse:collapse}
        th{text-align:left;font-size:11px;text-transform:uppercase;color:#64748b;padding:8px 12px;border-bottom:2px solid #e8eaf6}
        td{padding:10px 12px;border-bottom:1px solid #f1f5f9;font-size:13px}
        tfoot td{font-weight:700;border-top:2px solid #e8eaf6;border-bottom:none}
        @media print{@page{margin:20mm}}
      </style></head><body>
      <h1>Time Report — ${firstName}</h1>
      <p>${data.label}</p>
      <div class="summary">
        <div><div class="val" style="color:#2563eb">${data.total_display}</div><div class="lbl">Total encoded</div></div>
        <div><div class="val" style="color:#7c3aed">${data.days_logged}</div><div class="lbl">Days logged</div></div>
        <div><div class="val" style="color:#059669">${data.average_display}</div><div class="lbl">Avg per day</div></div>
      </div>
      <table>
        <thead><tr><th>Date</th><th>Day</th><th>Activity</th><th style="text-align:right">Time</th><th style="text-align:right">Decimal</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr>
          <td colspan="3">Total</td>
          <td style="text-align:right;color:#2563eb;font-family:monospace">${data.total_display}</td>
          <td style="text-align:right;font-family:monospace;color:#64748b">${(data.total_seconds/3600).toFixed(2)}h</td>
        </tr></tfoot>
      </table>
      <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
    </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`${API}/api/jira/worklogs/report/daily?month=${month}&member_name=${encodeURIComponent(member)}`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [month, member]);

  const maxSeconds = useMemo(
    () => Math.max(1, ...(data?.days || []).map(day => day.seconds)),
    [data],
  );

  const firstName = member.split(' ')[0];

  return (
    <div>
      {selectedDay && <DayDetail date={selectedDay} member={member} onClose={() => setSelectedDay(null)} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>Encoded time</h2>
          <div style={{ marginTop: 4, color: 'var(--text-2)', fontSize: 13 }}>
            Personal Jira worklogs for {member.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            className="upload-select"
            value={member}
            onChange={e => setMember(e.target.value)}
            style={{ minWidth: 180 }}
          >
            {TEAM_MEMBERS.map(m => (
              <option key={m} value={m}>{m.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}</option>
            ))}
          </select>
          <button className="btn-secondary" onClick={() => setMonth(value => shiftMonth(value, -1))}>←</button>
          <input
            type="month"
            value={month}
            max={localMonthStr(new Date())}
            onChange={event => setMonth(event.target.value)}
            style={{ padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', color: 'var(--text)', fontFamily: 'DM Sans' }}
          />
          <button
            className="btn-secondary"
            disabled={month >= localMonthStr(new Date())}
            onClick={() => setMonth(value => shiftMonth(value, 1))}
          >→</button>
          {data && data.days.length > 0 && (<>
            <button className="btn-secondary" onClick={() => exportCSV(data, member)} title="Export CSV" style={{ padding: '7px 12px' }}>⬇ CSV</button>
            <button className="btn-secondary" onClick={printReport} title="Print / Save as PDF" style={{ padding: '7px 12px' }}>🖨 PDF</button>
            <button
              className="btn-secondary"
              onClick={sendEmail}
              disabled={emailSending}
              title="Send by email"
              style={{ padding: '7px 12px', color: emailStatus === 'ok' ? '#059669' : emailStatus === 'err' ? '#c62828' : undefined }}
            >
              {emailSending ? '…' : emailStatus === 'ok' ? '✓ Sent' : emailStatus === 'err' ? '✗ Failed' : '✉ Email'}
            </button>
          </>)}
        </div>
      </div>

      {loading && <div className="card" style={{ color: 'var(--text-2)' }}>Loading report…</div>}
      {error && <div className="card" style={{ color: 'var(--critical)' }}>Could not load report: {error}</div>}

      {!loading && !error && data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
            {[
              ['Total encoded', data.total_display, '#2563eb'],
              ['Days with time', data.days_logged, '#7c3aed'],
              ['Average per day', data.average_display, '#059669'],
            ].map(([label, value, color]) => (
              <div className="card" key={label} style={{ padding: 18 }}>
                <div style={{ color: 'var(--text-2)', fontSize: 12, marginBottom: 7 }}>{label}</div>
                <div style={{ color, fontFamily: 'DM Mono', fontSize: 25, fontWeight: 700 }}>{value}</div>
                <div style={{ color: 'var(--text-2)', fontSize: 11, marginTop: 5 }}>{data.label}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--border)', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Daily totals — {firstName}</span>
              <span style={{ fontWeight: 400, color: 'var(--text-2)', fontSize: 12 }}>Click a day for details</span>
            </div>
            {data.days.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-2)' }}>No encoded time for this month.</div>
            ) : (
              <div>
                {data.days.map(day => (
                  <div
                    key={day.date}
                    onClick={() => setSelectedDay(day.date)}
                    style={{
                      display: 'grid', gridTemplateColumns: '150px minmax(160px, 1fr) 90px 75px',
                      alignItems: 'center', gap: 16, padding: '12px 18px',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {new Date(`${day.date}T12:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                      <div style={{ color: 'var(--text-2)', fontSize: 11, marginTop: 2 }}>{day.weekday}</div>
                    </div>
                    <div style={{ height: 8, borderRadius: 99, background: 'var(--surface-2)', overflow: 'hidden' }}>
                      <div style={{ width: `${(day.seconds / maxSeconds) * 100}%`, height: '100%', borderRadius: 99, background: '#2563eb' }} />
                    </div>
                    <div style={{ textAlign: 'right', fontFamily: 'DM Mono', fontWeight: 700 }}>{day.display}</div>
                    <div style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'DM Mono', fontSize: 12 }}>{decimalHours(day.seconds)}h</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
